/**
 * THE ANSWER TO A WRITE THAT DID NOT HAPPEN — one component, every screen.
 *
 * The sibling of `<DenialNotice>`, `<CapacityRefusalNotice>` and `<RoleRefusalNotice>`, and it does
 * not replace any of them: it DELEGATES to them. A denial is still rendered by the denial component,
 * a ceiling refusal by the ceiling one, a capacity refusal by the capacity one. What this adds is the
 * routing — so that a screen performing a write has one line of markup instead of a five-branch
 * ternary that the next screen copies and shortens.
 *
 * That copying is the actual risk. The Owner's direction survives only while every screen keeps the
 * states apart, and a `catch { setFailed(true) }` in the twelfth screen erases it silently for that
 * screen. Here, the erasure would have to be done once, in a file whose whole subject is not doing it.
 *
 * ── WHAT THE CALLER STILL OWNS ──────────────────────────────────────────────────────────────────
 *
 * The CONFLICT branch. A conflict body means different things per route — an excess of capabilities,
 * a capacity limit, a set of missing configuration fields, a lifecycle transition — and only the
 * screen knows which route it called. So a screen passes `renderConflict`, which is where its own
 * mapper turns the body into Hebrew. There is a written fallback for a conflict a screen did not
 * anticipate, and it says the two things that are always true: the server refused, and it refused
 * before writing, so nothing changed.
 *
 * NOTHING HERE DECIDES PERMISSION. It renders a decision that arrived.
 */

import type { ReactNode } from 'react';
import type { ActionFailure } from '../api/actionOutcome';
import { NOT_FOUND_HE, refusalHe, UNREACHABLE_HE } from '../api/actionOutcome';
import { stepUpTextFor } from '../api/stepUpText';
import { DenialNotice } from './DenialNotice';
import { ErrorState, StateBlock } from './StateBlock';
import { StepUpPrompt } from './StepUpPrompt';
import { routes } from '../routes';

/** Where each step-up sends the person. Two of the three are two DIFFERENT screens (see stepUpText). */
const STEP_UP_HREF: Readonly<Record<ReturnType<typeof stepUpTextFor>['action'], string>> = {
  sign_in_again: routes.signIn,
  challenge: routes.mfaChallenge,
  enrolment: routes.mfaEnrolment,
};

export interface ActionFailureNoticeProps {
  readonly failure: ActionFailure;
  /**
   * The label of the control that completes the action, for the second-factor card to name.
   * **Section 6, 2026-10-28.** See `StepUpPrompt`.
   */
  readonly continueLabelHe?: string;
  /**
   * The screen's own reading of a conflict body. Returning `null` falls through to the written
   * generic — which is honest rather than silent, and is what a route this screen has not taught the
   * mapper about produces.
   */
  readonly renderConflict?: (failure: Extract<ActionFailure, { kind: 'conflict' }>) => ReactNode;
  /**
   * What the SCREEN knows about an unclassifiable failure of ITS action. **2026-10-25.**
   *
   * The `error` branch is reached when the server answered in a way the client cannot name, and the
   * component genuinely does not know what the action was. The screen does: it knows that this call
   * was an MFA confirmation, that a registration window exists, and that starting again is a real
   * next step. Passing that in is how the Owner's rule — say what happened and what to do now — is
   * met without this file guessing on behalf of twenty screens.
   *
   * Omitted, the default states plainly that the cause is not known. That is deliberate: a generic
   * guess is wrong precisely when it matters.
   */
  /**
   * Run after a step-up has been satisfied inline. **Section 3, 2026-10-25.**
   *
   * A step-up reaches this component by TWO routes and both are forwarded: the gate's own refusal
   * arrives as `denied` with reason `stepup_required`, and a handler's 403 arrives as `stepUp` with
   * a `need`. Wiring only one of them would leave the control present on some screens and absent on
   * others for a reason nobody could see from the screen.
   */
  readonly onStepUpSatisfied?: () => void;
  readonly errorGuidance?: {
    readonly whatHappenedHe: string;
    readonly whatToDoHe: string;
    /** An action rendered inside the failure block — e.g. a button that restarts the flow. */
    readonly action?: ReactNode;
  };
}

export function ActionFailureNotice({
  failure,
  renderConflict,
  errorGuidance,
  onStepUpSatisfied,
  continueLabelHe,
}: ActionFailureNoticeProps): JSX.Element {
  if (failure.kind === 'denied') {
    // The gate's own body, through the gate's own component. Uniform outside the tenant.
    // `onStepUpSatisfied` travels with it: a `stepup_required` denial is completed in place.
    return (
      <DenialNotice
        deny={failure.body}
        {...(onStepUpSatisfied === undefined ? {} : { onStepUpSatisfied })}
      />
    );
  }

  if (failure.kind === 'stepUp') {
    if (failure.need === null) {
      // The server required a step-up and named a challenge this build has no words for. Saying less
      // beats naming the wrong screen — a person sent to enrol when they hold a working authenticator
      // ends up with a second enrolled device.
      return (
        <StateBlock
          titleHe="נדרש אימות נוסף לפעולה הזו"
          bodyHe="השרת דורש אימות נוסף לפני הפעולה, והפעולה לא בוצעה."
          tone="error"
          nextStep={<a className="btn btn--nav" href={routes.personalSecurity}>מסך האבטחה שלי</a>}
        />
      );
    }
    /*
     * A handler's own 403, which names the challenge. When it is the SECOND FACTOR and the screen
     * can retry, it is satisfied here rather than at another address — see `StepUpPrompt`. The
     * ENROLMENT need still goes to the enrolment screen, because there is nothing to type yet.
     */
    if (failure.need === 'MFA' && onStepUpSatisfied !== undefined) {
      return (
        <section className="denial" data-tone="identity" role="alert">
          <h2 className="denial__title">נדרש אימות נוסף</h2>
          <StepUpPrompt
            onSatisfied={onStepUpSatisfied}
            {...(continueLabelHe === undefined ? {} : { continueLabelHe })}
          />
        </section>
      );
    }
    const text = stepUpTextFor(failure.need);
    return (
      <StateBlock
        titleHe={text.titleHe}
        bodyHe={text.explanationHe}
        tone={text.tone === 'warn' ? 'unavailable' : 'error'}
        nextStep={
          <a className="btn btn--nav" href={STEP_UP_HREF[text.action]}>
            {text.nextStepHe}
          </a>
        }
      />
    );
  }

  if (failure.kind === 'conflict') {
    const rendered = renderConflict?.(failure);
    if (rendered !== undefined && rendered !== null) return <>{rendered}</>;
    return (
      <StateBlock
        titleHe="הפעולה לא בוצעה"
        // Both halves are true whichever conflict it was, and both are worth more than "אירעה תקלה".
        bodyHe="השרת סירב לבקשה. הסירוב נעשה לפני הכתיבה, ולכן לא נשמר שום שינוי."
        tone="error"
        nextStep={<p>אפשר לרענן את המסך ולראות את המצב העדכני לפני ניסיון נוסף.</p>}
      />
    );
  }

  if (failure.kind === 'refused') {
    /*
     * THE SERVER'S OWN SENTENCE, SHOWN. *"\u05d0\u05dd \u05d7\u05e1\u05e8\u05d4 \u05d4\u05e8\u05e9\u05d0\u05d4, \u05d4\u05e6\u05d2 \u05d6\u05d0\u05ea \u05d1\u05de\u05d3\u05d9\u05d5\u05e7; \u05d0\u05dd \u05d6\u05d5 \u05ea\u05e7\u05dc\u05d4, \u05ea\u05e7\u05df
     * \u05d0\u05d5\u05ea\u05d4."* The route wrote a remedy for the person and this renders it rather than a
     * reference code.
     *
     * THE DETAIL IS A SECOND LINE AND NOT THE TITLE. For a clearing refusal it is the provider's
     * own code and description -- useful, and not a sentence a customer should be asked to read
     * first.
     *
     * AN EMPTY REMEDY FALLS BACK TO THE GENERIC ERROR STATE, because a title with no body is worse
     * than an honest "something went wrong, here is the reference".
     */
    /*
     * ── THE HEBREW SENTENCE COMES FIRST, AND `remedy` IS THE FALLBACK. **2026-11-04.** ─────────
     *
     * `remedy` is written on the SERVER, in English, for the process log and for an operator
     * reading a response body. It was being rendered to the customer verbatim: the Owner was
     * shown `this office's plan has no price on the published price book` on his own screen, and
     * before that `this office has no live subscription to attach a card to`.
     *
     * A REFUSAL WORD IS A CLOSED VOCABULARY AND A SENTENCE IS NOT. So the word is translated here
     * and the server's prose is used only where this file has nothing — which keeps a new refusal
     * visible instead of silent, and keeps the English out of the screen for every refusal anybody
     * has written a sentence for.
     */
    const saidHe = refusalHe(failure.reason);
    if (saidHe !== null || failure.remedy !== '') {
      return (
        <StateBlock
          titleHe={saidHe ?? failure.remedy}
          bodyHe={failure.detail ?? ''}
          tone="error"
          nextStep={<p>{`\u05d0\u05e1\u05de\u05db\u05ea\u05d0: ${failure.requestUid}`}</p>}
        />
      );
    }
  }

  if (failure.kind === 'notFound') {
    return (
      <StateBlock
        titleHe={NOT_FOUND_HE.titleHe}
        bodyHe={NOT_FOUND_HE.bodyHe}
        tone="unavailable"
        nextStep={<p>{NOT_FOUND_HE.nextStepHe}</p>}
      />
    );
  }

  if (failure.kind === 'unreachable') {
    return (
      <StateBlock
        titleHe={UNREACHABLE_HE.titleHe}
        bodyHe={UNREACHABLE_HE.bodyHe}
        tone="error"
        nextStep={<p>{UNREACHABLE_HE.nextStepHe}</p>}
      />
    );
  }

  /*
   * 09 §5.8 / §9.1 V3: never the exception text. And, since 2026-10-25, never the reference code on
   * its own either — `ErrorState` says what happened and what to do, and the screen supplies both
   * when it knows them.
   */
  return (
    <ErrorState
      requestUid={failure.requestUid}
      {...(errorGuidance === undefined
        ? {}
        : {
            whatHappenedHe: errorGuidance.whatHappenedHe,
            whatToDoHe: errorGuidance.whatToDoHe,
            ...(errorGuidance.action === undefined ? {} : { action: errorGuidance.action }),
          })}
    />
  );
}
